import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { 
  ChevronLeft, ChevronRight, Phone, MessageSquare, MapPin, Navigation,
  Clock, Plus, User, Sparkles, Check, X, RotateCcw, AlertTriangle, CalendarDays
} from 'lucide-react';
import { Button } from '../components/ui/button';

const STATUS_STYLES = {
  confirmed:   { bg: 'bg-emerald-500/12', border: 'border-emerald-500/30', text: 'text-emerald-400', label: 'Confirmed' },
  pending:     { bg: 'bg-amber-500/12', border: 'border-amber-500/30', text: 'text-amber-400', label: 'Pending' },
  ai_booked:   { bg: 'bg-violet-500/12', border: 'border-violet-500/30', text: 'text-violet-400', label: 'AI Booked' },
  pending_review: { bg: 'bg-orange-500/12', border: 'border-orange-500/30', text: 'text-orange-400', label: 'Needs Review' },
  cancelled:   { bg: 'bg-red-500/12', border: 'border-red-500/30', text: 'text-red-400', label: 'Cancelled' },
  completed:   { bg: 'bg-slate-500/12', border: 'border-slate-500/30', text: 'text-slate-400', label: 'Completed' },
  default:     { bg: 'bg-blue-500/12', border: 'border-blue-500/30', text: 'text-blue-400', label: 'Scheduled' },
};

function getStatus(evt) {
  if (evt.status === 'pending_review') return 'pending_review';
  if (evt.status === 'cancelled') return 'cancelled';
  if (evt.status === 'completed') return 'completed';
  if (evt.ai_booked) return 'ai_booked';
  if (evt.status === 'pending') return 'pending';
  if (evt.status === 'confirmed') return 'confirmed';
  return 'default';
}

function fmtTime(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }); }
  catch { return ''; }
}

function isSameDay(d1, d2) {
  return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
}

function isPastEvent(evt) {
  const d = new Date(evt.start_time || evt.start || 0);
  return d < new Date();
}

// ═══════════════════ MOBILE EVENT CARD ═══════════════════
function MobileEventCard({ evt, onSelect, onCallback, onReview }) {
  const status = getStatus(evt);
  const s = STATUS_STYLES[status] || STATUS_STYLES.default;
  const past = isPastEvent(evt);
  const title = evt.summary || evt.lead_name || 'Appointment';
  const leadName = evt.lead_name || '';
  const location = evt.location || '';
  const phone = evt.lead_phone || '';
  const startTime = fmtTime(evt.start_time || evt.start);
  const endTime = fmtTime(evt.end_time || evt.end);
  const eventType = evt.event_type || evt.appointment_type || '';

  return (
    <div
      role="button" tabIndex={0}
      onClick={() => onSelect(evt)}
      className={`rounded-2xl border ${s.border} ${s.bg} p-4 transition-all active:scale-[0.98] ${past ? 'opacity-50' : ''}`}
      data-testid={`mobile-event-${evt.id || ''}`}
    >
      {/* Time + Status */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Clock size={13} className="text-muted-foreground" />
          <span className="text-sm font-semibold text-foreground">{startTime}</span>
          {endTime && <span className="text-xs text-muted-foreground">— {endTime}</span>}
        </div>
        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${s.bg} ${s.text} border ${s.border}`}>
          {status === 'ai_booked' && <Sparkles size={8} className="inline mr-0.5" />}
          {s.label}
        </span>
      </div>

      {/* Title + Lead */}
      <p className="text-base font-semibold text-foreground leading-tight">{title}</p>
      {leadName && leadName !== title && (
        <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1">
          <User size={12} />{leadName}
        </p>
      )}

      {/* Location + Type */}
      <div className="flex items-center gap-3 mt-2 flex-wrap">
        {location && (
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <MapPin size={11} />{location}
          </span>
        )}
        {eventType && (
          <span className="text-xs text-muted-foreground capitalize">{eventType}</span>
        )}
      </div>

      {/* Quick Actions */}
      {status === 'pending_review' && onReview ? (
        <div className="flex gap-2 mt-3">
          <button onClick={e => { e.stopPropagation(); onReview(evt, 'approve'); }}
            className="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold py-2.5 rounded-xl bg-emerald-500/15 text-emerald-400 active:bg-emerald-500/30"
            data-testid="mobile-approve-btn">
            <Check size={14} />Accept
          </button>
          <button onClick={e => { e.stopPropagation(); onReview(evt, 'reject'); }}
            className="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold py-2.5 rounded-xl bg-red-500/15 text-red-400 active:bg-red-500/30"
            data-testid="mobile-reject-btn">
            <X size={14} />Reject
          </button>
        </div>
      ) : !past && (
        <div className="flex gap-2 mt-3">
          {phone && (
            <a href={`tel:${phone}`} onClick={e => e.stopPropagation()}
              className="flex items-center justify-center gap-1 text-xs font-semibold py-2.5 px-4 rounded-xl bg-emerald-500/15 text-emerald-400 active:bg-emerald-500/30"
              data-testid="mobile-call-btn">
              <Phone size={13} />Call
            </a>
          )}
          {phone && (
            <a href={`sms:${phone}`} onClick={e => e.stopPropagation()}
              className="flex items-center justify-center gap-1 text-xs font-semibold py-2.5 px-4 rounded-xl bg-blue-500/15 text-blue-400 active:bg-blue-500/30"
              data-testid="mobile-text-btn">
              <MessageSquare size={13} />Text
            </a>
          )}
          {location && (
            <a href={`https://maps.google.com/?q=${encodeURIComponent(location)}`} onClick={e => e.stopPropagation()} target="_blank" rel="noopener noreferrer"
              className="flex items-center justify-center gap-1 text-xs font-semibold py-2.5 px-4 rounded-xl bg-sky-500/15 text-sky-400 active:bg-sky-500/30"
              data-testid="mobile-directions-btn">
              <Navigation size={13} />Go
            </a>
          )}
          {onCallback && (
            <button onClick={e => { e.stopPropagation(); onCallback(evt); }}
              className="flex items-center justify-center gap-1 text-xs font-semibold py-2.5 px-4 rounded-xl bg-violet-500/15 text-violet-400 active:bg-violet-500/30"
              data-testid="mobile-reschedule-btn">
              <RotateCcw size={13} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════ SWIPEABLE DAY STRIP ═══════════════════
function DayStrip({ selectedDate, onSelectDate, events }) {
  const stripRef = useRef(null);
  const today = useMemo(() => new Date(), []);

  const days = useMemo(() => {
    const arr = [];
    for (let i = -14; i <= 30; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      arr.push(d);
    }
    return arr;
  }, [today]);

  // Auto-scroll to selected date
  useEffect(() => {
    if (!stripRef.current) return;
    const idx = days.findIndex(d => isSameDay(d, selectedDate));
    if (idx >= 0) {
      const el = stripRef.current.children[idx];
      if (el) el.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }, [selectedDate, days]);

  return (
    <div ref={stripRef}
      className="flex gap-1 overflow-x-auto pb-2 px-2 scrollbar-hide snap-x snap-mandatory"
      style={{ WebkitOverflowScrolling: 'touch' }}
      data-testid="mobile-day-strip"
    >
      {days.map((d, i) => {
        const active = isSameDay(d, selectedDate);
        const isT = isSameDay(d, today);
        const hasEvts = events.some(e => isSameDay(new Date(e.start_time || e.start), d));
        const isPast = d < today && !isT;

        return (
          <button key={i}
            onClick={() => onSelectDate(d)}
            className={`flex-shrink-0 w-[52px] py-2 rounded-2xl text-center transition-all snap-center
              ${active ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/30 scale-105' 
                : isT ? 'bg-slate-800 border-2 border-emerald-500/40 text-emerald-400' 
                : isPast ? 'text-slate-600' 
                : 'text-slate-400 active:bg-slate-800'}`}
            data-testid={`day-${d.getDate()}`}
          >
            <div className="text-[10px] font-medium uppercase tracking-wider">
              {d.toLocaleDateString('en-US', { weekday: 'short' })}
            </div>
            <div className={`text-lg font-bold ${active ? '' : isT ? '' : ''}`}>{d.getDate()}</div>
            {hasEvts && !active && (
              <div className="flex justify-center mt-0.5">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
              </div>
            )}
            {active && hasEvts && (
              <div className="flex justify-center mt-0.5">
                <div className="w-1.5 h-1.5 rounded-full bg-white" />
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ═══════════════════ MAIN MOBILE CALENDAR ═══════════════════
export default function MobileCalendar({ events, onSelectEvent, onCallback, onReview, onNewAppointment, onGoToday }) {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [touchStart, setTouchStart] = useState(null);
  const contentRef = useRef(null);
  const today = useMemo(() => new Date(), []);

  const dayEvents = useMemo(() => {
    return events
      .filter(e => isSameDay(new Date(e.start_time || e.start), selectedDate))
      .sort((a, b) => new Date(a.start_time || a.start) - new Date(b.start_time || b.start));
  }, [events, selectedDate]);

  const pendingCount = dayEvents.filter(e => e.status === 'pending_review').length;
  const isToday = isSameDay(selectedDate, today);

  // Find next upcoming event
  const nextEventIdx = useMemo(() => {
    if (!isToday) return -1;
    const now = new Date();
    return dayEvents.findIndex(e => new Date(e.start_time || e.start) > now);
  }, [dayEvents, isToday]);

  // Swipe to change day
  const handleTouchStart = (e) => setTouchStart(e.touches[0].clientX);
  const handleTouchEnd = (e) => {
    if (touchStart === null) return;
    const diff = touchStart - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 60) {
      const d = new Date(selectedDate);
      d.setDate(d.getDate() + (diff > 0 ? 1 : -1));
      setSelectedDate(d);
    }
    setTouchStart(null);
  };

  const navMonth = (dir) => {
    const d = new Date(selectedDate);
    d.setMonth(d.getMonth() + dir);
    setSelectedDate(d);
  };

  return (
    <div className="flex flex-col h-full" data-testid="mobile-calendar">
      {/* ── STICKY HEADER ── */}
      <div className="sticky top-0 z-20 bg-background/95 backdrop-blur-lg border-b border-border/30 pb-2">
        {/* Month + Year + Actions */}
        <div className="flex items-center justify-between px-4 pt-3 pb-2">
          <div className="flex items-center gap-2">
            <button onClick={() => navMonth(-1)} className="p-1.5 rounded-xl active:bg-slate-800">
              <ChevronLeft size={18} className="text-muted-foreground" />
            </button>
            <h2 className="text-lg font-bold text-foreground" data-testid="mobile-month-title">
              {selectedDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </h2>
            <button onClick={() => navMonth(1)} className="p-1.5 rounded-xl active:bg-slate-800">
              <ChevronRight size={18} className="text-muted-foreground" />
            </button>
          </div>
          <div className="flex items-center gap-2">
            {!isToday && (
              <Button onClick={() => { setSelectedDate(new Date()); if (onGoToday) onGoToday(); }}
                variant="outline" size="sm"
                className="text-xs h-8 rounded-xl border-emerald-500/30 text-emerald-400"
                data-testid="mobile-today-btn">
                Today
              </Button>
            )}
            <Button onClick={onNewAppointment} size="sm"
              className="bg-emerald-500 hover:bg-emerald-600 text-white text-xs h-8 rounded-xl"
              data-testid="mobile-new-btn">
              <Plus size={14} />
            </Button>
          </div>
        </div>

        {/* Day Strip */}
        <DayStrip selectedDate={selectedDate} onSelectDate={setSelectedDate} events={events} />
      </div>

      {/* ── CONTENT AREA ── */}
      <div ref={contentRef}
        className="flex-1 overflow-y-auto px-4 pt-3 pb-24"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        data-testid="mobile-events-list"
      >
        {/* Selected Day Label */}
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold text-muted-foreground">
            {isToday ? 'Today' : selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
            {dayEvents.length > 0 && (
              <span className="ml-2 text-xs font-normal">
                {dayEvents.length} event{dayEvents.length !== 1 ? 's' : ''}
              </span>
            )}
          </p>
        </div>

        {/* Pending Review Banner */}
        {pendingCount > 0 && (
          <div className="rounded-2xl border border-orange-500/30 bg-orange-500/8 p-3 mb-3 flex items-center gap-2"
            data-testid="mobile-pending-banner">
            <AlertTriangle size={16} className="text-orange-400 flex-shrink-0" />
            <span className="text-sm text-orange-300 font-medium">
              {pendingCount} AI booking{pendingCount > 1 ? 's' : ''} need{pendingCount === 1 ? 's' : ''} review
            </span>
          </div>
        )}

        {/* Event Cards */}
        {dayEvents.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 rounded-2xl bg-slate-800/80 flex items-center justify-center mb-4">
              <CalendarDays size={28} className="text-slate-500" />
            </div>
            <p className="text-sm font-medium text-muted-foreground mb-1">No events</p>
            <p className="text-xs text-slate-600 mb-4">
              {isToday ? 'Your day is clear' : 'Nothing scheduled'}
            </p>
            <Button onClick={onNewAppointment} variant="ghost" size="sm"
              className="text-emerald-400 text-xs rounded-xl"
              data-testid="mobile-add-event-btn">
              <Plus size={14} className="mr-1" />Add appointment
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {dayEvents.map((evt, i) => (
              <div key={evt.id || evt.google_event_id || i}
                className={i === nextEventIdx ? 'relative' : ''}
              >
                {i === nextEventIdx && (
                  <div className="absolute -left-4 top-0 bottom-0 w-1 bg-emerald-500 rounded-r-full" />
                )}
                <MobileEventCard
                  evt={evt}
                  onSelect={onSelectEvent}
                  onCallback={onCallback}
                  onReview={onReview}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
